import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';

// The Staff activity log, newest first, optionally filtered to one target User or Workspace
// (?user= or ?workspace=). Targets aren't foreign keys, so their names are looked up after.
export function StaffActivity() {
  const [params] = useSearchParams();
  const user = params.get('user');
  const workspace = params.get('workspace');

  const { data, isLoading, error } = useQuery({
    queryKey: ['staff-activity', user, workspace],
    queryFn: async () => {
      let query = supabase
        .from('staff_activity')
        .select('id, action, target_user_id, target_workspace_id, details, created_at, staff:staff_id(email)')
        .order('created_at', { ascending: false })
        // ponytail: newest 200 only, add paging when the log outgrows it
        .limit(200);
      if (user) query = query.eq('target_user_id', user);
      if (workspace) query = query.eq('target_workspace_id', workspace);
      const rows = await query;
      if (rows.error) throw rows.error;

      const ids = (key: 'target_user_id' | 'target_workspace_id', extra: string | null) =>
        [...new Set([extra, ...rows.data.map((r) => r[key])].filter((v): v is string => !!v))];
      const [users, workspaces] = await Promise.all([
        supabase.from('profiles').select('id, email').in('id', ids('target_user_id', user)),
        supabase.from('workspaces').select('id, name').in('id', ids('target_workspace_id', workspace)),
      ]);
      if (users.error) throw users.error;
      if (workspaces.error) throw workspaces.error;

      return {
        rows: rows.data,
        userEmail: new Map(users.data.map((u) => [u.id, u.email ?? u.id])),
        workspaceName: new Map(workspaces.data.map((w) => [w.id, w.name])),
      };
    },
  });

  if (isLoading) return <div className="loading-spinner" />;
  if (error) return <p className="text-size-small staff-error">{error.message}</p>;
  if (!data) return null;

  const { rows, userEmail, workspaceName } = data;
  const filter = user
    ? `User ${userEmail.get(user) ?? user}`
    : workspace
      ? `Workspace ${workspaceName.get(workspace) ?? workspace}`
      : null;

  return (
    <div className="staff-detail">
      <h1 className="text-size-large">Activity</h1>
      {filter && (
        <p className="text-size-small">
          Only activity on {filter}. <Link to="/staff/activity">Show all</Link>
        </p>
      )}
      <div className="staff-results">
        <section>
          {rows.length === 0 && <p className="text-size-small text-colour-alt-grey">No activity.</p>}
          {rows.map((r) => (
            <div key={r.id} className="staff-result">
              <span>
                {r.staff?.email ?? 'Unknown Staff'} · {r.action}
                {r.target_user_id && (
                  <>
                    {' · '}
                    <Link to={`/staff/users/${r.target_user_id}`}>
                      {userEmail.get(r.target_user_id) ?? r.target_user_id}
                    </Link>
                  </>
                )}
                {r.target_workspace_id && (
                  <>
                    {' · '}
                    <Link to={`/staff/workspaces/${r.target_workspace_id}`}>
                      {workspaceName.get(r.target_workspace_id) ?? r.target_workspace_id}
                    </Link>
                  </>
                )}
                {r.details && typeof r.details === 'object' && !Array.isArray(r.details) && (
                  <span className="text-colour-alt-grey">
                    {Object.entries(r.details).map(([k, v]) => ` · ${k}: ${v ?? 'none'}`)}
                  </span>
                )}
              </span>
              <span className="text-colour-alt-grey">{new Date(r.created_at).toLocaleString()}</span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
